/* eslint-disable no-console */
import { MessageType, SystemOptionType, PredefinedOptionType } from '../enum';
import { EventType, ItemType, buildArticleOption, buildConnectConfig, canConnectToAgent, createMessageHistory, generateUserSideArticleMessageId, getCategoryTitleFromMessageHistory, getSectionTitleFromMessageHistory, isMessageUserSideArticle, randomId, spawnSystemMessage, spawnUserSystemMessage, spawnCategoryListMessage, spawnArticleCheckFailureMessage, spawnAlternativeActionsMessage, hasMessage, spawnUserSearchArticleMessage, buildConnectToAgentWithArticleConfig, checkABTestingPassed, canCheckMetadataAutomation, randomUUID } from '../lib';
import { ActionSelectedEvent, ArticleInteractionExternEvent, ArticleSystemOption, BaseEvent, BaseExternalEvent, BaseMessage, BaseSystemMessage, CancelSupportRequestExternEvent, ConnectToAgent, MessageConfiguration, RollbackEvent, SystemListMessage, SystemOption, SystemOptionEvent, ArticleListWithOtherNavigationMessage, ArticleListNavigationEvent, PreSupportSelectionEvent, AlternativeActionSelectedEvent, ResolutionEvent, ApplyMetadataAutomationExternEvent, PredictiveSuggestionSelectionEvent, MessageListItem } from '../types';
import { MessageStoreApi, MessageUtil } from './messages';
import * as PleasedApi from '@/common/api/api.pleased';
import { Switcher, switcher } from './switcher';
import { isCategoryOption, isSectionOption, isArticleOption } from '../lib/option';
import { i18n } from '@/plugins/i18n';
import mitt, { Emitter } from 'mitt';
import { PrePromptConditions, conditionHelper, generateUpdatedEvent, isPromise, noop, toArr } from './ChatFramework.utils';
import { ChatFrameworkInterceptor, QueuedMessage, InterceptorMetadata, ChatFrameworkOptions, ChatFrameworkPreConfig, ChatFrameworkDynamicOptions, ChatFrameworkEvents, UserDynamicOptions, GuestDynamicOptions, AiDynamicOptions } from './ChatFramework.types';

import { CHECKS } from './ChatMessageChecks.static';
import { resolveArticleAction, checkActionSupported } from './ChatMessageActions.static';
import { resolvePredictiveMessages, getPredictionById } from './ChatMessagePredictiveSuggestions.static';
import { ResolutionType } from '../enum/ResolutionType';
import { analytics, EVENT_TYPES, FEATURES_EVENTS } from '@/common/logs';
import { AiService } from './ai-service/AiService';

export class ChatFramework {
  protected messageList!: MessageStoreApi;
  protected interceptors: ChatFrameworkInterceptor[] = [];

  protected lifecycle: 'created' | 'running' | 'paused' = 'created';
  protected emitter = mitt<ChatFrameworkEvents>();

  private aiService: AiService;

  protected eventSwitch!: Switcher<BaseEvent>;
  protected externEventSwitch!: Switcher<BaseExternalEvent>;

  protected preConfig!: ChatFrameworkPreConfig;

  protected options: ChatFrameworkDynamicOptions = { 
    canRunCheck: false, 
    ai: { 
      aiEnabledPerBrand: false, 
      aiEnabledPerSession: true, 
      openAiDown: false 
    }, 
    user: {}, 
    guest: {} 
  };

  get $e (): Pick<Emitter<ChatFrameworkEvents>, 'on'| 'off'> {
    return this.emitter;
  }

  get status () {
    return this.lifecycle;
  }

  constructor (options: ChatFrameworkOptions) {
    this.messageList = options.messages;
    this.interceptors = this.interceptors.concat(options.interceptors || []);
    this.setOptions(options.dynamicOptions || this.options);
    this.setupEventSwitch();

    this.aiService = new AiService({
      messageList: this.messageList,
      emitter: this.emitter,

      getMessages: this.getMessages.bind(this),
      addMessage: this.add.bind(this),
      removeMessage: this.remove.bind(this),

      getOptions: () => this.options,
      setAiOptions: this.setAiOptions.bind(this),
      submit: this.submit.bind(this),
      submitExternalEvent: this.submitExternalEvent.bind(this),

      connectToAgentFromConfig: this.connectToAgentFromConfig.bind(this),
      connectArticleWithAcknowledgement: this.connectArticleWithAcknowledgement.bind(this),
    });
  }

  protected setupEventSwitch () {
    this.eventSwitch = switcher<BaseEvent>()
      .when(EventType.isOptionEvent, (event) => {
        const selected = event.payload.selected;
        this.emitter?.emit('selectOption', { selected, message: event.message, event });

        if (isCategoryOption(selected)) return this.onCategorySelected(event);
        if (isSectionOption(selected)) return this.onSectionSelected(event);
        if (isArticleOption(selected)) return this.onArticleSelected(event);
      })
      .when(EventType.isArticleListNavigationEvent, (event) => {
        const selected = event.payload.selected;
        this.emitter?.emit('selectOption', { selected, message: event.message, event });

        if (isArticleOption(selected)) {
          return this.onArticleSelected(event);
        }
        if (selected === PredefinedOptionType.BROWSE_OTHER_ARTICLES) return this.openSectionsFromArticleList(event);
        // Directly connect through messages
        if (selected === PredefinedOptionType.ISSUE_NOT_LISTED) this.connectToAgentFromIssueNotListed(event);
      })
      .when(EventType.isRollbackEvent, this.rollback)
      .when(EventType.isActionSelectedEvent, this.executeAction)
      .when(EventType.isPredictiveSuggestionSelectionEvent, this.resolvePredictiveSelection)
      .when(EventType.isPreSupportPromptSelectionEvent, evt => {
        const selected = evt.payload.selected;
        this.emitter?.emit('selectOption', { selected, message: evt.message, event: evt });

        if (selected === PredefinedOptionType.CONNECT_TO_SUPPORT) return this.connectToAgentFromEvent(evt);
        if (selected === PredefinedOptionType.BROWSE_OTHER_ARTICLES) return this.onExploreOtherArticlesFromPreSupportPrompt(evt);
        if (selected === PredefinedOptionType.RESTART_CHAT) return this.restartChat();
        if (selected === PredefinedOptionType.END_CHAT) return this.endConversationFlow();
        if (selected === PredefinedOptionType.POSITIVE_RESOLUTION) return this.positiveResolutionPreSupportPrompt(evt);
        if (selected === PredefinedOptionType.NEED_MORE_HELP) return this.connectToAgentFromEvent(evt);
        if (selected === PredefinedOptionType.SEARCH_ARTICLES) return this.emitter.emit('openAutocomplete');

        // Predictive option other issue event.
        if (selected === PredefinedOptionType.NO_OTHER_ISSUE) {
          // TODO: Move to a function, the switch junction is not
          // a place to have lot of logic.
          const { message, payload: { selected } } = evt;
          this.updateMessageSelection(message, selected);
          return this.addCategoryMessage();
        }
      })

      .when(EventType.isAlternativeActionEvent, this.navigateAlternativeAction)
      .when(EventType.isResolutionEvent, this.navigateResolution)
      .none(e => console.error('[CF] Unhandled event passed', { e }));

    this.externEventSwitch = switcher<BaseExternalEvent>()
      .when(EventType.isArticleInteractionEvent, evt => {
        this.emitter?.emit('selectExternalOption', { selected: evt.payload.interaction });

        if (evt.payload.interaction === 'back') return this.onArticleBackToChatroom(evt);
        if (evt.payload.interaction === 'support-request') return this.onArticleConnect(evt);
        if (evt.payload.interaction === 'execute-action') return this.onArticleActionExecute(evt);
        if (evt.payload.interaction === 'restart-chat') return this.restartChat();
        if (evt.payload.interaction === 'go-to-my-requests') return this.restartChat();
      })
      .when(EventType.isCancelSupportRequestEvent, this.onCancelSupportRequest)
      .when(EventType.isApplyMetadataAutomationEvent, this.onApplyMetadataAutomation)
      .none(e => console.error('[CF] Unhandled external event passed', { e }));
  }

  setPreConfig (config: ChatFrameworkPreConfig) {
    this.preConfig = config;
  }

  setOptions (options: Pick<ChatFrameworkDynamicOptions, 'canRunCheck' | 'enablePredictiveOptions'>) {
    this.options = { ...this.options, ...options };
  }

  setUserOptions (options: UserDynamicOptions) {
    this.options.user = { ...this.options.user, ...options };
  }

  setGuestOptions (options: GuestDynamicOptions) {
    this.options.guest = { ...this.options.guest, ...options };
  }

  setAiOptions (options: AiDynamicOptions) {
    this.options.ai = { ...this.options.ai, ...options };
  }

  start () {
    const hasCategoryMessage = hasMessage(this.messageList.get(), { id: 'category-base' });
    if (hasCategoryMessage || this.lifecycle === 'running') return;
    if (!this.preConfig) {
      throw new Error('[CF] Cannot start without preConfig');
    }
    // this.addCategoryMessage();
    this.pseudoStart();
  }

  submit (event: BaseEvent) {
    // First check if event should be handled by AI service separately / AI only related event.
    if (this.aiService.testAiEventHandler(event)) {
      return this.aiService.submitAiEvent(event);
    }

    // Then check if we should connect to AI instead of common event handler.
    if (this.aiService.testAiConnectEventHandler(event)) {
      return this.aiService.submitAiConnectEvent(event);
    }

    this.eventSwitch.match(event);
  }

  submitExternalEvent = (event: BaseExternalEvent) => {
    // First check if event should be handled by AI service separately / AI only related event.
    if (this.aiService.testAiEventHandler(event)) {
      return this.aiService.submitAiEvent(event);
    }

    // Then check if we should connect to AI instead of common external event handler.
    if (this.aiService.testAiConnectExternalEventHandler(event)) {
      return this.aiService.submitAiConnectExternalEvent(event);
    }

    this.externEventSwitch.match(event);
  }

  protected addCategoryMessage () {
    const config = this.preConfig;
    this.add(spawnCategoryListMessage({
      message: config.welcomeMessage,
      options: config.categories.map(c => ({ ...c, kind: SystemOptionType.CATEGORY })),
      id: 'category-base',
    }), { tags: ['from:system'] });
  }

  // #region Methods for checking Predictive options.
  pseudoStart () {
    if (!this.options.enablePredictiveOptions) return this.addCategoryMessage();
    // Run predictive options text
    this.add(this.resolvePredictiveOptions().then(x => {
      if (!toArr(x).length) this.addCategoryMessage();
      return x;
    }).catch((e) => {
      console.warn(`${e}  Resolving predictive options failed`);
      this.addCategoryMessage();
      return [];
    }));
  }

  async resolvePredictiveOptions () {
    const message = await resolvePredictiveMessages(this.preConfig.predictiveConfig).catch(() => []);
    const messages = toArr(message || []).flat();
    if (!messages.length) return messages;

    // One predictive option has matched.
    const matched = messages.filter(m => (m as BaseSystemMessage).config?.matchedPredictiveOption)[0] as BaseSystemMessage;
    if (matched.config?.matchedPredictiveOption) this.emitter.emit('notify:prediction:match', { type: matched.config?.matchedPredictiveOption, message: matched });
    return messages;
  }

  public checkCanConnectToAI (connectConfig: ConnectToAgent) {
    return this.aiService.checkCanConnectToAI(connectConfig);
  }
  // #endregion

  protected resolvePredictiveSelection = (event: PredictiveSuggestionSelectionEvent) => {
    const { message, payload: { selected } } = event;
    // Add predictive option resolution
    this.updateMessageSelection(message, selected);
    const predictionId = message.config?.matchedPredictiveOption!;

    this.emitter?.emit('selectOption', { selected, message: event.message, event });
    this.emitter.emit('notify:prediction:select', { message, selected, type: predictionId });

    if (selected === PredefinedOptionType.NO_OTHER_ISSUE) { return this.addCategoryMessage(); }
    // if (selected === PredefinedOptionType.CONNECT_TO_SUPPORT) {
    // Add user side issue.
    // Add a message to indicate predictive option has matched.
    const uiMessage = getPredictionById(predictionId)?.ui() || '';
    const userMessage = this.getUserMessage(uiMessage, event);
    this.add(userMessage, { event, tags: ['from:visitor', 'prediction.match'] });
    // Check if there is article navigation.
    const articleId = message.config?.connect?.redirectToArticleId;
    if (articleId) {
      // Show the article id first.
      this.emitter.emit('openArticle', { articleId, title: '', sourceMessageId: message.id as string });
      this.emitter.emit('notify:prediction:go:article', { message, articleId, type: predictionId });
      return;
    }
    // No article navigation, redirect to connect to agent.
    this.connectToAgentFromMessage(message);
  }

  protected onCategorySelected = (event: SystemOptionEvent) => {
    const {
      payload: { selected: selectedCategory },
      message,
    } = event;

    const userSelectedCategoryMessage = this.getUserMessage(selectedCategory.title, event);

    // Mark it as selected.
    this.messageList.update(spawnSystemMessage<SystemListMessage>({ ...message, selectedOption: selectedCategory }));

    this.add(userSelectedCategoryMessage, { event, tags: ['from:visitor', 'opt.category'] });

    const categoryMessage = PleasedApi.getCategoryDetails
      .cached({ categoryId: selectedCategory.id })
      .then(({ data: { id: sectionId, categoryConfig, sections, articles } }) => {
        // Here is an indirect dependency on the state of the chatbot, can be removed when refactoring.
        const articleOptions = articles.map(a => (buildArticleOption({ ...a, category: selectedCategory.title }))).map(({ action, ...x }) => action && checkActionSupported(action) ? { ...x, action: resolveArticleAction(action) } : x);
        const sectionOptions = sections.map((s) => ({ ...s, kind: SystemOptionType.SECTION }));

        // Fallback to old UI if there are no articles configured in the category.
        const config: MessageConfiguration = {
          // noneOfTheAbove: buildNoneOfTheAboveConfig(noneOfTheAboveConfig),
          connect: { ...buildConnectConfig(categoryConfig), category: selectedCategory.title },
          // resolution: buildResolution(categoryConfig),
        };

        const categoryPassAbTesting = checkABTestingPassed({
          userId: this.options.user.userId,
          abValue: categoryConfig.abValue || '',
          enableAbTesting: categoryConfig.enableAbTesting || false,
          articleId: categoryConfig.id,
        });

        return !articleOptions.length ? spawnSystemMessage<SystemListMessage>({
          type: MessageType.SECTION_LIST,
          id: `sections-${sectionId}-${randomId()}`,
          options: sectionOptions,
          config,
          message: categoryConfig.responseMessage || '',
          history: createMessageHistory(event, message),
        }) : spawnSystemMessage<ArticleListWithOtherNavigationMessage>({
          type: MessageType.ARTICLE_LIST_WITH_OTHER_NAVIGATION,
          id: `articles-or-sections-${sectionId}-${randomId()}`,
          options: articleOptions,
          config,
          sections: sectionOptions || [],
          elements: {
            browseArticles: Boolean(sectionOptions.length),
            issueNotListed: canConnectToAgent(config.connect) && categoryPassAbTesting,
          },
          message: categoryConfig.responseMessage || '',
          history: createMessageHistory(event, message),
        });
      }
      );

    this.add(categoryMessage, { event, tags: ['from:system', 'fetching', 'opt.category'] });
  }

  protected rollback = (event: RollbackEvent) => {
    this.emitter?.emit('selectOption', { selected: event.payload.selected, message: event.message, event });
    MessageUtil.removeMessagesAfter(this.messageList, event.message);
  }

  protected openConnectFromMyIssueNotSelected (event: BaseEvent) {
    // Need to know the category.
    const { message: evtMessage } = event;
    const message = evtMessage as ArticleListWithOtherNavigationMessage;

    this.connectArticleWithAcknowledgement({
      supportOptions: message.config?.connect as ConnectToAgent,
      event,
      history: createMessageHistory(event, message),
    });
  }

  protected openSectionsFromArticleList = (event: BaseEvent) => {
    // Need to know the category.
    const { message: evtMessage } = event;
    const message = evtMessage as ArticleListWithOtherNavigationMessage;

    const sections = message.sections;
    const userResponse = this.getUserMessage(i18n.t('chat.v2.user.section.other_articles') as string, event);
    this.add(userResponse, { event, tags: ['from:visitor'] });

    const updatedMessage = { ...message, selectedOption: PredefinedOptionType.BROWSE_OTHER_ARTICLES };

    this.messageList.update(updatedMessage);

    const sectionMessage = spawnSystemMessage<SystemListMessage>({
      type: MessageType.SECTION_LIST,
      options: sections,
      config: evtMessage.config,
      message: i18n.t('chat.v2.sys.section.more_sections_title') as string,
      history: createMessageHistory(event, message),
    });

    this.add(sectionMessage, { event, tags: ['from:system'] });
  }

  protected getUserMessage (helpTopic: string, event?: BaseEvent) {
    return spawnUserSystemMessage({
      sendToChat: true,
      message: i18n.t('chat.user.response.message.text', { name: helpTopic }) as string,
      interactiveMessageParts: [
        i18n.t('chat.user.response.message.part', { name: helpTopic }) as string,
        helpTopic,
      ],
      ...(event ? { history: createMessageHistory(event, event.message) } : {})
      ,
    });
  }

  protected onSectionSelected = async (event: SystemOptionEvent) => {
    const {
      payload: { selected: section },
      message,
    } = event;

    const parentConfig = message.config?.connect;

    const userMessage = this.getUserMessage(section.title, event);
    this.add(userMessage, { event, tags: ['from:visitor', 'opt.section'] });

    const loadingSectionMessage = PleasedApi.getSectionDetails
      .cached({ sectionId: section.id })
      .then(({ data: { sectionConfig, sections, articles } }) => {
        const responseMessage = sectionConfig.responseMessage || '';

        const mappedSections: SystemOption[] = (sections || []).map((s) => ({ ...s, kind: SystemOptionType.SECTION }));
        const mappedArticles: SystemOption[] = (mappedSections.length ? [] : articles).map((a) => (buildArticleOption({
          ...a,
          category: parentConfig?.category || '',
          section: section.title,
        }))).map(({ action, ...x }) => action && checkActionSupported(action) ? { ...x, action: resolveArticleAction(action) } : x);

        const options = mappedSections.concat(mappedArticles);
        const config = sectionConfig.overrideParentConfig ? buildConnectConfig(sectionConfig) : parentConfig;

        const messageToShow = spawnSystemMessage<SystemListMessage>({
          type: MessageType.HYBRID_LIST,
          config: { ...message.config, connect: { ...config!, section: section.title } },
          options,
          message: responseMessage,
          history: createMessageHistory(event, message, userMessage),
        });

        return messageToShow;
      });

    this.add(loadingSectionMessage, { event, tags: ['from:system', 'fetching', 'opt.section'] });
  }

  protected onArticleSelected = async (event: ArticleListNavigationEvent|SystemOptionEvent) => {
    const { payload: { selected }, message } = event;
    const option = selected as ArticleSystemOption;
    const updatedMessage = { ...message, selectedOption: selected };

    this.messageList.update(updatedMessage);

    const parentWithArticleConfig = buildConnectToAgentWithArticleConfig(message?.config?.connect!, option);

    this.addArticleUserMessage(option, event);

    // There are 2 flows here, if the user has a check, or go to article page.
    // If article is hidden, we move user to agent directly.
    if (canConnectToAgent(parentWithArticleConfig) && (option.overrideParentConnectAgent && option.hideContent && !option.checkActionId)) {
      const fetchMetadata = parentWithArticleConfig.enableMetadataRules && this.options.user.isUserLoggedIn;
      const metadataRulesResult = await (fetchMetadata ? PleasedApi.getMetadataAutomations({ articleId: option.id }).catch(noop) : Promise.resolve({ data: [] }));
      const metadataRules = metadataRulesResult?.data || [];
      this.connectToAgentFromConfig({ ...parentWithArticleConfig, metadataRules });
      return;
    }

    if (!option.checkActionId || ItemType.isArticleCheckFailureMessage(message)) {
      // No checks necessary, open the article page

      this.emitter.emit('openArticle', {
        articleId: option.id,
        title: option.title,
        sourceMessageId: message.id as string,
        ...(ItemType.isArticleCheckFailureMessage(message) ? { useConfig: message.config?.connect } : {}),
      });

      return;
    }
    // If checks are present, then get the check config.

    const nextMessage = PleasedApi.getArticleDetails.cached({ articleId: option.checkActionId }).then(({ data: fetchedArticle }) => {
      const article = fetchedArticle;
      const category = fetchedArticle.category;

      const checkArticleOption = buildArticleOption({
        ...article.articleConfig,
        id: article.id,
        title: article.title,
        category: category?.titleTranslated || category?.title || '',
      });
      return this.runCheck({ check: checkArticleOption.checks || '' }).then(checkPassed => ({ checkPassed, checkArticleOption }));
    }).then(({ checkPassed, checkArticleOption }) => {
      if (checkPassed) {
        // Check if content is hidden
        if (canConnectToAgent(parentWithArticleConfig) && option.hideContent) {
          this.connectToAgentFromConfig(parentWithArticleConfig);
        } else {
          this.emitter.emit('openArticle', { articleId: option.id, title: option.title, sourceMessageId: message.id as string });
        }
        // Don't queue any message. Returning empty array here will ensure that no messages are queued.
        return [];
      }
      // Check not passed. show the corresponding action.
      const slots = CHECKS.KYC.slots();
      const articleAction = checkActionSupported(checkArticleOption.action) ? { action: resolveArticleAction(checkArticleOption.action!) } : {};
      return spawnArticleCheckFailureMessage({
        message: slots.message,
        article: { ...checkArticleOption, ...articleAction },
        messageSlots: {
          afterArticle: slots.actionMessage,
        },
        config: {
          connect: parentWithArticleConfig,
          ...articleAction,
        },
      });
    });

    this.add(nextMessage, { event, tags: ['fetching', 'from:system', 'check.running'] });
  }

  protected addArticleUserMessage (option: ArticleSystemOption, event: BaseEvent|BaseExternalEvent) {
    const conversation = this.messageList.get().filter(m => !ItemType.isPreSupportPromptMessage(m));

    const lastMessage = conversation[conversation.length - 1];
    const userSideArticleId = generateUserSideArticleMessageId(option.id);
    const isArticleMessageAlreadyExists = isMessageUserSideArticle(lastMessage?.id, option.id);

    let spawnMessageFactory = spawnUserSystemMessage;
    if (EventType.isArticleInteractionEvent(event) && !event.payload.sourceMessageId) {
      // if there is no source message id, this message is probably from article search.
      spawnMessageFactory = spawnUserSearchArticleMessage;
    }

    if (!isArticleMessageAlreadyExists) {
      const userMessage = spawnMessageFactory({
        id: userSideArticleId,
        message: i18n.t('chat.user.response.message.text', { name: option.title }) as string,
        interactiveMessageParts: [i18n.t('chat.user.response.message.part') as string, option.title],
        history: EventType.isExternalEvent(event) ? this.generateArticleMessageHistory(event as ArticleInteractionExternEvent) : createMessageHistory(event, event.message),
      });

      // Looks like you have checked out our article, you can create the new
      this.add(userMessage, { event, tags: ['from:visitor', 'opt.article'] });
    }
  }

  protected generateArticleMessageHistory (event: ArticleInteractionExternEvent) {
    const sourceMessage = event.payload.sourceMessageId ? this.messageList.get().find(m => m.id === event.payload.sourceMessageId) as BaseMessage || null : null;
    const history = sourceMessage ? createMessageHistory(event, sourceMessage) : undefined;
    return history;
  }

  protected connectToAgentFromIssueNotListed (event: ArticleListNavigationEvent) {
    const hardcodedConnectConfigParams = { showDescriptionBox: true };
    const updatedEvent = generateUpdatedEvent(event, hardcodedConnectConfigParams);
    this.add(
      spawnUserSystemMessage({
        sendToChat: true,
        message: i18n.t('chat.sys.msg.issue_not_listed') as string,
        ...(updatedEvent ? { history: createMessageHistory(updatedEvent, updatedEvent.message) } : {})
        ,
      })
    );

    this.connectToAgentFromEvent(updatedEvent);
  }

  protected connectToAgentFromEvent (event: ArticleListNavigationEvent|PreSupportSelectionEvent|AlternativeActionSelectedEvent) {
    const KNOWN_SELECTION_VALIDATION = [PredefinedOptionType.CONNECT_TO_SUPPORT, PredefinedOptionType.ISSUE_NOT_LISTED];
    if (!KNOWN_SELECTION_VALIDATION.includes(event.payload.selected as any)) {
      console.warn('[CF] The connect to agent from event is coming from unrecognized event', { event });
    }

    const { message, payload: { selected } } = event;
    this.updateMessageSelection(message, selected);

    this.connectArticleWithAcknowledgement({
      history: createMessageHistory(event, event.message),
      supportOptions: event.message.config?.connect!,
      event,
    });
  }

  protected positiveResolutionPreSupportPrompt (evt: PreSupportSelectionEvent) {
    this.updateMessageSelection(evt.message, PredefinedOptionType.POSITIVE_RESOLUTION);
    this.add(spawnUserSystemMessage({ message: i18n.t('chat.v2.user.my_issue_resolved') as string }));
    const message = spawnSystemMessage({
      type: MessageType.PRE_SUPPORT_PROMPT_BARE,
      message: i18n.t('chat.v2.sys.glad_to_help') as string,
      history: createMessageHistory(evt, evt.message),
    });
    this.add(message, { event: evt, tags: ['from:system'] });
  }

  protected onExploreOtherArticlesFromPreSupportPrompt (evt: PreSupportSelectionEvent) {
    this.updateMessageSelection(evt.message, PredefinedOptionType.BROWSE_OTHER_ARTICLES);
    const message = spawnSystemMessage({
      type: MessageType.PRE_SUPPORT_PROMPT_BARE,
      message: '',
      history: createMessageHistory(evt, evt.message),
    });
    this.add(message, { event: evt, tags: ['from:system'] });
  }

  protected updateMessageSelection (message: BaseSystemMessage, selected: string|SystemOption) {
    const updatedMessage = { ...message, selectedOption: selected };
    this.messageList.update(updatedMessage);
  }

  protected connectArticleWithAcknowledgement = ({ history, supportOptions, event }: ({ supportOptions: ConnectToAgent, history?: BaseMessage['history'], event?: BaseEvent|BaseExternalEvent})) => {
    const acknowledgement = spawnSystemMessage({
      type: MessageType.PRE_SUPPORT_ACKNOWLEDGEMENT,
      message: i18n.t('chat.v2.sys.lets_connect_to_agent') as string,
      history,
      config: { connect: supportOptions },
    });

    this.add(acknowledgement, { event, tags: ['article.connect'] });

    this.connectToAgentFromMessage(acknowledgement);
  }

  protected onArticleConnect = async (event: ArticleInteractionExternEvent) => {
    const { article: sourceArticleOption } = event.payload;
    const supportOptions = { ...event.payload.supportOptions };

    if (supportOptions.enableMetadataRules && this.options.user.isUserLoggedIn) {
      await PleasedApi.getMetadataAutomations({ articleId: sourceArticleOption.id }).then(({ data }) => {
        supportOptions.metadataRules = data;
      }).catch(noop);
    }

    this.addArticleUserMessage(sourceArticleOption, event);

    const history = this.generateArticleMessageHistory(event);
    this.connectArticleWithAcknowledgement({ history, supportOptions, event });
  }

  protected onArticleActionExecute (event: ArticleInteractionExternEvent) {
    console.error('[CF] Action executed from article pages (external actions outside chat messages) are not handled by the CF');
  }

  protected async onArticleBackToChatroom (event: ArticleInteractionExternEvent) {
    // Can be an external event. Not coming from message.
    // There are 3 messages here to add.
    // 1. Intermediate resolution.
    // 2. user side article search.
    const { article: sourceArticleOption } = event.payload;
    const supportOptions = { ...event.payload.supportOptions };

    if (supportOptions.enableMetadataRules && this.options.user.isUserLoggedIn) {
      await PleasedApi.getMetadataAutomations({ articleId: sourceArticleOption.articleId! }).then(({ data }) => {
        supportOptions.metadataRules = data;
      }).catch(noop);
    }

    this.addArticleUserMessage(sourceArticleOption, event);

    // Article has connect, but no action.

    const sourceMessage = event.payload.sourceMessageId ? this.messageList.get().find(m => m.id === event.payload.sourceMessageId) as BaseMessage || null : null;
    const history = sourceMessage ? createMessageHistory(event, sourceMessage) : undefined;

    const hasAction = Boolean(sourceArticleOption.action?.action);
    const hasSupport = canConnectToAgent(supportOptions) || this.aiService.checkCanConnectToAI(supportOptions);
    const hasMetadataRules = Boolean(supportOptions.enableMetadataRules && supportOptions.metadataRules);

    const connect: ConnectToAgent|undefined = supportOptions ? {
      ...supportOptions,
      category: supportOptions.category || sourceArticleOption?.category || (history ? getCategoryTitleFromMessageHistory(history) || '' : ''),
      section: supportOptions.section || sourceArticleOption?.section || (history ? getSectionTitleFromMessageHistory(history) || '' : ''),
    } : undefined;

    const { showConnectAgentMessage, connectAgentMessage } = supportOptions;

    if (showConnectAgentMessage && connectAgentMessage) {
      const connectMessage = spawnSystemMessage({
        type: MessageType.DEFAULT,
        message: connectAgentMessage,
      });

      this.add(connectMessage, { event, tags: ['from:system'] });
    }

    const nextMessage = switcher<PrePromptConditions, BaseSystemMessage>({ hasAction, hasSupport, hasMetadataRules })
      .when(conditionHelper({ hasAction: true, hasSupport: true }), () => spawnSystemMessage({
        type: MessageType.PRE_SUPPORT_PROMPT_ACTION_WITH_SUPPORT,
        message: i18n.t('chat.v2.sys.back_from_article') as string,
        config: {
          action: sourceArticleOption.action,
          connect,
        },
        history,
      }))
      .when(conditionHelper({ hasAction: false, hasSupport: false, hasMetadataRules: false }), () => spawnSystemMessage({
        type: MessageType.PRE_SUPPORT_PROMPT_BARE,
        message: i18n.t('chat.v2.sys.back_from_article') as string,
        history,
      }))
      .when(conditionHelper({ hasAction: false, hasSupport: true }), () => spawnSystemMessage({
        type: MessageType.PRE_SUPPORT_PROMPT_SUPPORT_ONLY,
        message: i18n.t('chat.v2.sys.back_from_article') as string,
        config: {
          connect,
        },
        history,
      }))
      .when(conditionHelper({ hasAction: true, hasSupport: false }), () => spawnSystemMessage({
        type: MessageType.PRE_SUPPORT_PROMPT_ACTION_ONLY,
        message: i18n.t('chat.v2.sys.back_from_article') as string,
        config: {
          action: sourceArticleOption.action,
        },
        history,
      }))
      .when(conditionHelper({ hasMetadataRules: true, hasSupport: false }), () => spawnSystemMessage({
        type: MessageType.PRE_SUPPORT_PROMPT_METADATA_ONLY,
        message: i18n.t('chat.v2.sys.back_from_article') as string,
        config: {
          connect,
          action: sourceArticleOption.action,
        },
        history,
      })).match();

    if (!nextMessage) {
      console.error('[CF]: Not able to determine next message when back from the article page.');
    }

    // We want to safely replace the message.
    this.add({ ...nextMessage, id: `${nextMessage.type}--(articleID=${sourceArticleOption.id})` }, { event, tags: ['from:system'] });
  }

  protected executeAction = (event: ActionSelectedEvent) => {
    this.emitter?.emit('selectOption', { selected: event.type, message: event.message, event });
    this.emitter.emit('goToAction', event.payload.action);
  }

  protected async runCheck ({ check }:{check: string}) {
    if (!this.options.canRunCheck) return true;
    return CHECKS.KYC.check();
  }

  protected navigateAlternativeAction = (event: AlternativeActionSelectedEvent) => {
    const { payload: { selected }, message: { config } } = event;
    this.emitter?.emit('selectOption', { selected, message: event.message, event });

    if (selected === PredefinedOptionType.RESTART_CHAT) return this.restartChat();
    if (selected === PredefinedOptionType.END_CHAT) return this.endConversationFlow();
    if (selected === PredefinedOptionType.CONNECT_TO_SUPPORT) return this.connectToAgentFromEvent(event);
    if (selected === PredefinedOptionType.GO_TO_TICKET) return this.emitter.emit('goToTicket', config?.directRequest);
  }

  protected navigateResolution = (event: ResolutionEvent) => {
    const { message } = event;

    this.emitter?.emit('selectOption', { selected: event.payload.selected, message: event.message, event });

    switcher<ResolutionEvent>(event)
      .when(evt => ItemType.isMetadataResolutionMessage(evt.message), this.handleMetadataResolution)
      .when(evt => ItemType.isAnyOtherHelpResolutionMessage(evt.message), this.handleAnyOtherHelpResolution)
      .none(() => console.error('[CF] Unhandled resolution event', { event, message }))
      .match();
  }

  private handleMetadataResolution = (event: ResolutionEvent) => {
    const { message, payload } = event;
    const wasHelpful = payload.selected === ResolutionType.YES;

    // Track metadata resolution
    this.emitter.emit('metadata:track', {
      helpful: wasHelpful,
      logTraceId: message.config?.connect?.metadataAutomationLogTraceId,
    });

    if (wasHelpful) {
      analytics.track({
        type: EVENT_TYPES.FEATURE,
        name: FEATURES_EVENTS.METADATA_AUTOMATION_HELPFUL,
        selection: message.config?.connect?.appliedMetadataAutomation?.id,
      });

      this.add(
        spawnSystemMessage({
          type: MessageType.ANY_OTHER_HELP_RESOLUTION,
          message: i18n.t('chat.sys.msg.resolution.help_anything_else') as string,
        })
      );
    } else {
      const canConnect = canConnectToAgent(message.config?.connect);

      analytics.track({
        type: EVENT_TYPES.FEATURE,
        name: FEATURES_EVENTS.METADATA_AUTOMATION_NOT_HELPFUL,
        selection: message.config?.connect?.appliedMetadataAutomation?.id,
        metadata: { canConnect },
      });

      this.add(
        spawnSystemMessage({
          type: canConnect
            ? MessageType.PRE_SUPPORT_PROMPT_FAILED_RESOLUTION_WITH_SUPPORT
            : MessageType.PRE_SUPPORT_PROMPT_FAILED_RESOLUTION,
          message: i18n.t('chat.sys.msg.resolution.failed_resolution') as string,
          config: { connect: message.config?.connect },
        })
      );
    }
  }

  private handleAnyOtherHelpResolution = (event: ResolutionEvent) => {
    const { selected } = event.payload;

    if (selected === ResolutionType.YES) {
      this.restartChat();
    } else if (selected === ResolutionType.NO) {
      this.endConversationFlow();
    }
  }

  protected onCancelSupportRequest = (event: CancelSupportRequestExternEvent) => {
    this.emitter.emit('selectExternalOption', { selected: 'cancel_support_request' });
    this.emitter.emit('cancelSupportRequest');

    const message = spawnUserSystemMessage({
      message: i18n.t('chat.user.issue.cancel_request') as string,
    });

    const { showConnectAgentMessage, connectAgentMessage } = event.payload.supportOptions || {};

    if (showConnectAgentMessage && connectAgentMessage) {
      const connectMessage = spawnSystemMessage({
        type: MessageType.DEFAULT,
        message: connectAgentMessage,
      });

      this.add(connectMessage);
    }

    this.add(message, { event, tags: ['cancel.support'] });
    const cancelSupportMessage = spawnAlternativeActionsMessage({
      config: { connect: event.payload.supportOptions, directRequest: event.payload.directRequest },
    });
    this.add(cancelSupportMessage, { event, tags: ['cancel.support'] });
  }

  protected onApplyMetadataAutomation = (event: ApplyMetadataAutomationExternEvent) => {
    const { payload: {
      metadataAutomationMessage,
      metadataAutomationRule,
      transaction,
    } } = event;

    const supportOptions: ConnectToAgent | null = event.payload.supportOptions
      ? {
        ...event.payload.supportOptions,
        appliedMetadataAutomation: metadataAutomationRule,
        metadataAutomationLogTraceId: randomUUID(),
      }
      : null;

    const showMetadataMessage = Boolean(metadataAutomationRule && metadataAutomationMessage);

    this.emitter.emit('selectExternalOption', { selected: 'metadata_automation' });
    this.emitter.emit('metadata:track', {
      metadataAutomationId: showMetadataMessage ? metadataAutomationRule?.id : undefined,
      transactionId: transaction.tradeId,
      messageShown: showMetadataMessage,
      logTraceId: supportOptions?.metadataAutomationLogTraceId,
      metadataAutomationMessage,
    });

    if (!showMetadataMessage) {
      const hasAction = Boolean(supportOptions?.action?.action);

      if (!supportOptions || !canConnectToAgent(supportOptions)) {
        this.add(spawnSystemMessage({
          type: hasAction ? MessageType.PRE_SUPPORT_PROMPT_ACTION_ONLY : MessageType.PRE_SUPPORT_PROMPT_BARE,
          message: i18n.t('chat.v2.sys.back_from_article') as string,
        }), { event });
      }

      return;
    }

    const responseMessage = spawnSystemMessage({
      type: MessageType.DEFAULT,
      message: metadataAutomationMessage,
    });

    const resolutionMessage = spawnSystemMessage({
      type: MessageType.METADATA_RESOLUTION,
      message: i18n.t('chat.sys.msg.resolution.metadata_resolution') as string,
      config: { connect: supportOptions },
    });

    this.add([responseMessage, resolutionMessage], { event });
  }

  protected connectToAgentFromConfig (connectConfig?: ConnectToAgent | null) {
    if (!connectConfig) {
      console.error('[CF] No connect config provided.');
      return false;
    }
    if (!canConnectToAgent(connectConfig) && !canCheckMetadataAutomation(connectConfig)) {
      console.warn('[CF] Incorrect connect to agent config provided. Cannot connect to agent with this config or check metadata.');
      return false;
    }

    if (connectConfig.appliedMetadataAutomation) {
      this.emitter.emit('metadata:track', {
        connectedAgent: true,
        logTraceId: connectConfig.metadataAutomationLogTraceId,
      });
    }

    this.emitter.emit('connect', { options: connectConfig });
    return true;
  }

  protected connectToAgentFromMessage (message: BaseSystemMessage) {
    const connectionResult = this.connectToAgentFromConfig(message.config?.connect);
    if (!connectionResult) {
      console.error('[CF] Cannot connect to agent from message', { message });
    }
  }

  protected endConversationFlow () {
    this.add(
      [
        spawnUserSystemMessage({ message: i18n.t('chat.v2.user.lets_end_chat') as string }),
        spawnSystemMessage({ message: i18n.t('chat.sys.goodbye') as string, type: MessageType.DEFAULT }),
      ],
    );
    this.emitter.emit('endChat');
  }

  restartChat () {
    this.emitter.emit('notify:restartChat');
    this.messageList.clear();
    this.addCategoryMessage();
  }

  protected add (messages: QueuedMessage, event?: InterceptorMetadata) {
    if (!messages) return;

    const finalMessages = this.interceptors.reduce((acc: QueuedMessage, interceptor) => interceptor.intercept(acc, event) || acc, messages);
    if (!isPromise(finalMessages)) return this.messageList.addSafe(...toArr(finalMessages));
    return MessageUtil.addAsync(this.messageList, finalMessages);
  }

  protected remove (messages: MessageListItem | MessageListItem[]) {
    if (!messages) return;
    return this.messageList.remove(...toArr(messages));
  }

  protected getMessages () {
    return this.messageList.get();
  }
}

// event routers.
// messageListApi.
// OptionsStore.
// Common Actions.
// Dependent Actions.
// Mutually dependent actions.
// ChatFramework.
// Emit events.
